import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api-mysql';
import AddAchiever from './AddAchiever';
import ManageAchievers from './ManageAchievers';
import getImageUrl from '../utils/imageUrl';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [achievers, setAchievers] = useState([]);
  const [statistics, setStatistics] = useState({
    totalAchievers: 0,
    totalCompanies: 0,
    currentYear: 0,
    totalImages: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin-login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [achieversResponse, statsResponse] = await Promise.all([
        api.get('/achievers'),
        api.get('/statistics')
      ]);
      
      setAchievers(achieversResponse.data);
      setStatistics(statsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin-login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin-login');
  };

  const getCourseStatistics = () => {
    const courseList = [
      'Project Management Professional (PMP)®',
      'Certified Associate in Project Management (CAPM)®',
      'Program Management Professional (PgMP)®',
      'Portfolio Management Professional (PfMP)®',
      'PMI Agile Certified Practitioner (PMI-ACP)®',
      'PMI Risk Management Professional (PMI-RMP)®',
      'PMI Professional in Business Analysis (PMI-PBA)®'
    ];

    const courseCounts = {};
    courseList.forEach(course => {
      courseCounts[course] = achievers.filter(a => a.course === course).length;
    });

    return courseCounts;
  };

  const renderDashboard = () => {
    const courseStats = getCourseStatistics();
    
    return (
      <div>
        <div className="row">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm bg-white rounded-3 mb-4">
              <div className="card-body text-center">
                <i className="bi bi-trophy display-4 text-primary mb-3"></i>
                <h3 className="fw-bold">{statistics.totalAchievers}</h3>
                <p className="text-muted mb-0">Total Achievers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Statistics */}
        <div className="card border-0 shadow-sm bg-white rounded-3 mb-4">
          <div className="card-header bg-white border-0">
            <h5 className="mb-0">
              <i className="bi bi-bar-chart-fill me-2"></i>Achievers by Course
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {Object.entries(courseStats).map(([course, count]) => {
                const courseShortNames = {
                  'Project Management Professional (PMP)®': 'PMP',
                  'Certified Associate in Project Management (CAPM)®': 'CAPM',
                  'Program Management Professional (PgMP)®': 'PgMP',
                  'Portfolio Management Professional (PfMP)®': 'PfMP',
                  'PMI Agile Certified Practitioner (PMI-ACP)®': 'PMI-ACP',
                  'PMI Risk Management Professional (PMI-RMP)®': 'PMI-RMP',
                  'PMI Professional in Business Analysis (PMI-PBA)®': 'PMI-PBA'
                };
                
                const shortName = courseShortNames[course];
                
                return (
                  <div className="col-md-6 col-lg-4" key={course}>
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="fw-bold text-primary mb-1">{shortName}</h6>
                            <small className="text-muted" style={{fontSize: '0.75rem'}}>{course}</small>
                          </div>
                          <div className="text-end">
                            <h4 className="fw-bold mb-0">{count}</h4>
                            <small className="text-muted">achievers</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRecentAchievers = () => {
    const recentAchievers = achievers.slice(-3).reverse();
    
    return (
      <div className="card border-0 shadow-sm bg-white rounded-3">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">
            <i className="bi bi-clock-history me-2"></i>Recent Achievers
          </h5>
        </div>
        <div className="card-body">
          {recentAchievers.length === 0 ? (
            <p className="text-muted text-center">No recent achievers</p>
          ) : (
            recentAchievers.map(achiever => (
              <div className="d-flex align-items-center mb-3" key={achiever.id}>
                <img 
                  src={getImageUrl(achiever.image)} 
                  alt={achiever.name} 
                  style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px', marginRight: '15px'}}
                />
                <div>
                  <h6 className="mb-1">{achiever.name}</h6>
                  <p className="mb-0 text-muted small">{achiever.company} • {achiever.role}</p>
                  <small className="text-primary">{achiever.month} {achiever.year}</small>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <i className="bi bi-arrow-clockwise"></i>
      </div>
    );
  }

  return (
    <div className="d-flex">
      {/* Sidebar */}
      <div className="admin-sidebar col-md-3 col-lg-2 p-3 d-flex flex-column">
        <div className="sidebar-brand text-center">
          <div className="brand-icon">
            <i className="bi bi-shield-check"></i>
          </div>
          <h5>Admin Panel</h5>
          <small>Achievers Management</small>
        </div>
        <nav className="nav flex-column px-2">
          <button
            className={`nav-link ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveSection('dashboard')}
          >
            <i className="bi bi-grid-1x2-fill me-2"></i>Dashboard
          </button>
          <button
            className={`nav-link ${activeSection === 'add-achiever' ? 'active' : ''}`}
            onClick={() => setActiveSection('add-achiever')}
          >
            <i className="bi bi-person-plus-fill me-2"></i>Add Achiever
          </button>
          <button
            className={`nav-link ${activeSection === 'manage-achievers' ? 'active' : ''}`}
            onClick={() => setActiveSection('manage-achievers')}
          >
            <i className="bi bi-people-fill me-2"></i>Manage Achievers
          </button>
        </nav>
        <div className="sidebar-footer px-2 mt-auto">
          <button className="nav-link logout-btn" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right me-2"></i>Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main-content col-md-9 col-lg-10">
        <div className="p-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Achievers Management</h2>
          </div>

          {/* Content Sections */}
          {activeSection === 'dashboard' && (
            <div>
              {renderDashboard()}
              {renderRecentAchievers()}
            </div>
          )}
          
          {activeSection === 'add-achiever' && (
            <AddAchiever onAchieverAdded={() => { fetchData(); setActiveSection('manage-achievers'); }} />
          )}
          
          {activeSection === 'manage-achievers' && (
            <ManageAchievers onAchieverUpdated={fetchData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
